import React, { useState } from 'react';

import { Check, Trash, X } from 'lucide-react';

import Flex from 'common/ui/Flex';
import Button from 'common/uiV3/Button';
import Tooltip from 'common/uiV3/Tooltip';

import AlertDialog from 'common/uiV3/AlertDialog';
import type { SpamRow as Row } from '../types';

type ActionsCellProps = {
  row: Row;
};

enum Action {
  approve = 'approve',
  reject = 'reject',
  delete = 'delete',
}

const SpamActionsCell = ({ row }: ActionsCellProps) => {
  const [loadingAction, setLoadingAction] = useState<Action | null>();

  const performApprove = async () => {
    setLoadingAction(Action.approve);
    await row.actions.approve();
    setLoadingAction(null);
  };

  const performReject = async () => {
    setLoadingAction(Action.reject);
    await row.actions.reject();
    setLoadingAction(null);
  };

  const performDelete = async () => {
    setLoadingAction(Action.delete);
    await row.actions.delete();
    setLoadingAction(null);
  };

  return (
    <Flex className="flex-col md:flex-row justify-between items-center gap-2">
      <AlertDialog>
        <AlertDialog.Trigger asChild>
          <Button
            aria-label="mark as spam"
            data-action="mark-as-spam"
            disabled={row.loading}
            isLoading={loadingAction === Action.reject}
            variant="outlined">
            <Check />
            Mark as spam
          </Button>
        </AlertDialog.Trigger>
        <AlertDialog.Content>
          <AlertDialog.Header>
            <AlertDialog.Title>
              Are you sure you want to ban and delete this user?
            </AlertDialog.Title>
            <AlertDialog.Description className="space-y-2">
              <p>The following action will:</p>
              <ul className="list-disc pl-4">
                <li>Delete all of their posts, comments, and votes.</li>
                <li>Ban them from voting, along with creating future posts and comments.</li>
              </ul>
              <p>This action cannot be&nbsp;reversed.</p>
            </AlertDialog.Description>
          </AlertDialog.Header>
          <AlertDialog.Footer>
            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
            <AlertDialog.Action asChild color="error">
              <Button
                type="button"
                isLoading={loadingAction === Action.reject}
                onClick={performReject}>
                Ban and delete user
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
      <Button
        aria-label="mark as not spam"
        disabled={row.loading}
        isLoading={loadingAction === Action.approve}
        onClick={performApprove}
        variant="outlined">
        <X />
        Not spam
      </Button>
      <Tooltip>
        <Tooltip.Trigger asChild>
          <Button
            aria-label="delete"
            isLoading={row.loading}
            variant="outlined"
            iconOnly
            onClick={performDelete}>
            <Trash />
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content>Delete {row.item.itemType}</Tooltip.Content>
      </Tooltip>
    </Flex>
  );
};

export default SpamActionsCell;
